<script>
import ForwardButton from "@/components/ForwardButton";
import BackwardButton from "@/components/BackwardButton";
import Tooltip from "../../components/tooltip/Tooltip.vue";
import vSelect from "vue-select";
import { keys, set, get } from "lodash";

export default {
  name: "Diversity",
  components: {
    ForwardButton,
    BackwardButton,
    vSelect,
    Tooltip,
  },
  data: () => ({
    options: [],
    payload: {
      gender: null,
      breed_id: null,
      is_egression: false,
      pcd_reability: false,
      disabilities_id: [],
    },
    errors: {
      gender: "",
      breed_id: "",
      is_egression: "",
      pcd_reability: "",
      disabilities_id: "",
    },
    gender_tooltip:
      "Ao pedir sua identidade de gênero durante o processo seletivo, garantimos a inclusão e diversidade no ambiente de trabalho. Além disso, isso pode ajudar a empresa a entender melhor a composição da sua força de trabalho, o que pode ser útil para estabelecer políticas e programas que promovam a diversidade e inclusão.",
    pcd_tooltip:
      "Durante o processo de seleção, perguntamos se você tem alguma deficiência para garantir que nosso ambiente de trabalho seja acessível para todos. Isso nos ajuda a cumprir a lei de cotas para pessoas com deficiência e a identificar quaisquer barreiras de acessibilidade que possam existir. Uma pessoa com deficiência reabilitada é alguém que passou pelo processo de reabilitação pelo INSS com sucesso e foi considerada apta a retornar ao mercado de trabalho.",
    skin_color_tooltip:
      "Pedir a sua cor durante o processo seletivo é uma forma de coletar informações sobre a diversidade étnico-racial da empresa e do seu quadro de colaboradores. Isso permite que a empresa possa identificar eventuais desigualdades e lacunas em sua força de trabalho e, a partir dessas informações, desenvolver políticas e ações afirmativas para promover a inclusão social e a diversidade em seu ambiente de trabalho.",
    rehabilitated_tooltip:
      "Pessoa egressa é quem saiu recentemente da prisão e pré-egressa é quem ainda está cumprindo pena, mas está em processo de reintegração na sociedade. Coletar informações sobre essas pessoas no processo seletivo é importante para promover a inclusão e diversidade no ambiente de trabalho, além de contribuir para reduzir preconceitos e discriminação.",
  }),
  computed: {
    egressed: {
      get() {
        return this.$store.state.diversity.egressed;
      },
      set(value) {
        this.$store.commit("diversity/ON_EGRESSED", value);
      },
    },
  },
  methods: {
    onEditMode() {
      const currentPath = this.$router.history.current.name;
      return currentPath.slice(0, 4) == "edit";
    },
    handleBackward() {
      if (this.onEditMode()) {
        this.$router.push({
          name: "dashboard-profile-detail",
        });
        return;
      }

      this.$store.dispatch("step/decrementCurrentStep");
      const step = this.$store.getters["step/current"];

      const { params } = this.$router.history.current;

      this.$router.push({
        name: step.path,
        params,
      });
    },
    async handleForward() {
      this.$vs.loading();
      this.$http
        .post("/api/v1/diversity", this.payload)
        .then(async (response) => {
          this.$vs.loading.close();
          await this.$store.dispatch("diversity/addDiversity", response.data);

          if (this.onEditMode()) {
            this.$router.push({
              name: "dashboard-profile-detail",
            });
            return;
          }

          await this.$store.dispatch("step/completeStep");
          await this.$store.dispatch("step/incrementCurrentStep");

          const step = this.$store.getters["step/current"];
          const { params } = this.$router.history.current;

          this.$router.push({
            name: step.path,
            params,
          });
        })
        .catch((error) => {
          get(error, 'response.data.errors', []).forEach((item) => {
            const { key, errors } = item;
            this.errors[key] = errors;
          });
          this.$vs.loading.close();
        });
    },
    fetchDiversityOptionsRequest() {
      this.$vs.loading();

      this.$http
        .get("/api/v1/diversity?debug=true")
        .then((response) => {
          const { data = [] } = response;

          const dataKeys = keys(data);

          const options = {};

          dataKeys.forEach((key) => {
            set(
              options,
              key,
              data[key].map((item) => ({
                label: item.name,
                value: item.id,
                description: item.description ? item.description : null,
              }))
            );
          });

          const {
            gender,
            breed,
            is_egression,
            pcd_reability,
            deficiencies,
          } = this.$store.state.diversity;

          set(this.payload, "gender", gender);

          if (breed && breed.id) {
            set(this.payload, "breed_id", breed.id);
          }

          if (is_egression !== null) {
            set(this.payload, "is_egression", is_egression);
          }

          if (pcd_reability !== null) {
            set(this.payload, "pcd_reability", pcd_reability);
          }

          if (deficiencies && deficiencies.length > 0) {
            set(
              this.payload,
              "disabilities_id",
              deficiencies.map((item) => item.id)
            );
          }

          this.options = options;

          this.$vs.loading.close();
        })
        .catch(() => {
          this.$vs.loading.close();
        });
    },
    onDisabilities(disabilityId) {
      if (!this.payload.disabilities_id.includes(disabilityId)) {
        this.payload.disabilities_id = [...this.payload.disabilities_id, disabilityId];
        return;
      }

      if (this.payload.disabilities_id.includes(disabilityId)) {
        this.payload.disabilities_id = this.payload.disabilities_id.filter(
          (el) => el != disabilityId
        );
        return;
      }
    },
  },
  created() {
    this.fetchDiversityOptionsRequest();
  },
};
</script>

<template>
  <div>
    <div class="card bg-white rounded">
      <vs-row class="my-4">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" vs-w="12">
          <feather-icon
            class="mr-4"
            icon="UsersIcon"
            svgClasses="text-black stroke-current w-36.96 h-36.96"
          />
          <h1 class="md:lg m-0">Diversidade</h1>
        </vs-col>
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" vs-w="12">
          <span class="my-2 md:text-base">
            Preencha seus dados para os recrutadores te conhecerem melhor.
          </span>
        </vs-col>
      </vs-row>
      <vs-row>
        <vs-col class="mt-4" vs-w="6" vs-xs="12" vs-sm="12">
          <vs-col vs-w="12">
            <div class="flex items-center my-2">
              <label class="vs-select--label">Identidade de gênero</label>
              <Tooltip :text="gender_tooltip" />
            </div>
            <v-select
              :options="options.genders"
              :reduce="(gender) => gender.value"
              v-model="payload.gender"
              placeholder="Selecione o gênero que se identifica"
            >
              <template v-slot:option="option">
                <div class="gender">
                  <h2>{{ option.label }}</h2>
                  <p>{{ option.description }}</p>
                </div>
              </template>
            </v-select>
            <span class="error" v-show="errors.gender.length">{{errors.gender}}</span>
          </vs-col>
          <vs-col class="mt-5" vs-w="12">
            <div class="flex items-center my-2">
              <label class="vs-select--label">Cor</label>
              <Tooltip :text="skin_color_tooltip" />
            </div>
            <v-select
              :options="options.breeds"
              :reduce="(breed) => breed.value"
              v-model="payload.breed_id"
              placeholder="Selecione sua cor"
            />
            <span class="error" v-show="errors.breed_id.length">{{errors.breed_id}}</span>
          </vs-col>
          <vs-col class="flex items-center mt-6" vs-w="12">
            <vs-switch color="primary" v-model="payload.is_egression" />
            <p class="ml-4">Pessoa egressa ou pré-egressa do sistema carcerário</p>
            <Tooltip :text="rehabilitated_tooltip" />
          </vs-col>
          <span class="error" v-show="errors.is_egression.length">{{errors.is_egression}}</span>
        </vs-col>
        <vs-col class="mt-4" vs-w="6" vs-xs="12" vs-sm="12">
          <vs-col vs-w="12">
            <div class="flex items-center mb-3">
              <vs-label>Possui algum tipo de deficiência?</vs-label>
              <Tooltip :text="pcd_tooltip" position="bottom-right" />
            </div>
            <div class="pcd__container">
              <vs-checkbox
                class="my-2"
                v-for="item in options.deficiency"
                :key="item.label"
                @click="onDisabilities(item.value)"
                :checked="payload.disabilities_id.includes(item.value)"
              >
                {{ item.label }}
              </vs-checkbox>
            </div>
            <span class="error" v-show="errors.disabilities_id.length">{{errors.disabilities_id}}</span>
          </vs-col>
          <vs-col class="flex items-center mt-2" vs-w="12">
            <vs-switch color="primary" v-model="payload.pcd_reability" />
            <p class="ml-4">Pessoa com deficiência reabilitada pelo INSS</p>
          </vs-col>
          <span class="error" v-show="errors.pcd_reability.length">{{errors.pcd_reability}}</span>
        </vs-col>
      </vs-row>
    </div>
    <div class="flex flex-wrap mt-2">
      <backward-button @click="handleBackward" />
      <forward-button @click="handleForward" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pcd__container {
  margin-left: -6px;
}
.gender {
  $text-color: #172b4d;
  $font-family: Roboto;

  margin: 0.3125rem auto;

  h2 {
    color: $text-color;
    font-family: $font-family;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%; /* 19.2px */
    letter-spacing: 0.32px;
  }

  p {
    color: $text-color;
    font-family: $font-family;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%; /* 16.8px */
    letter-spacing: 0.28px;
    text-wrap: wrap;
    margin: 0.625rem 0;
  }
}

.error {
  color: red;
  font-size: 12px;
  margin-top: 5px;
  margin-left: 5px;
}
</style>
